var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isCapitalUI } from 'react-common/theme';
import { BookerBase, padding } from '@buseditor/components';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tab, Tabs, Tooltip, Typography, useMediaQuery } from '@material-ui/core';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import dayjs from 'dayjs';
import { throttle } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { _T } from '../../../include/appI18n';
import { api } from '../../../include/network/network';
import Select from '../../../include/react-components/Select';
import { formatMoney, pluralizeAutoSplit } from '../../../include/utils';
import AsyncSpinner from '../../TourSearch/components/TourDescription/AsyncSpinner';
import { BookerViewControls } from './BookerViewControls';
import { useCartOrder } from './CartOrder';
import { InternalFlightBaggageCompact } from './FlightBaggageInfo';
import { renderSeatPricePopup } from './SeatPricePopup';
import { ServiceTouristsBlock } from './ServiceTouristsBlock';
const parseTimePart = (value) => {
    if (!value)
        return undefined;
    const match = value.match(/(\d{1,2}):(\d{2})/);
    if (!match)
        return undefined;
    const hours = match[1].padStart(2, '0');
    const minutes = match[2];
    return `${hours}:${minutes}`;
};
const formatTimeFromMoment = (value) => {
    if (!value.isValid())
        return undefined;
    if (value.hour() === 0 && value.minute() === 0 && value.second() === 0)
        return undefined;
    return value.format('HH:mm');
};
export function FlightService(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    const cartOrder = useCartOrder();
    const { service } = props;
    const useCombinedFlightSchemeModal = true;
    const useExtendedCartPageDesign = !!props.cart.configuration.useExtendedCartPageDesign;
    const displayFlightAsTransportation = !!((_a = props.cart.configuration.flightConfiguration) === null || _a === void 0 ? void 0 : _a.displayFlightAsTransportation);
    const showClass = !displayFlightAsTransportation;
    const deleteColSpan = showClass ? 5 : 4;
    const showExtendedInfo = useExtendedCartPageDesign && !!((_c = (_b = service.flightInfo) === null || _b === void 0 ? void 0 : _b.notes) === null || _c === void 0 ? void 0 : _c.trim());
    const isMininmalWidth = useMediaQuery('(max-width:900px)');
    const isMininmalHeight = useMediaQuery('(max-height:600px)');
    const [forceFullScreen, setForceFullScreen] = useState(false);
    const dialogFullScreen = useMemo(() => forceFullScreen || isMininmalWidth || isMininmalHeight, [forceFullScreen, isMininmalWidth, isMininmalHeight]);
    const beginDate = useMemo(() => moment(service.begin), [service.begin]);
    const endDate = useMemo(() => moment(service.end), [service.end]);
    const beginTime = useMemo(() => { var _a; return (_a = parseTimePart(service.scheduledDepartureTime)) !== null && _a !== void 0 ? _a : formatTimeFromMoment(beginDate); }, [service.scheduledDepartureTime, beginDate]);
    const endTime = useMemo(() => { var _a; return (_a = parseTimePart(service.scheduledArrivalTime)) !== null && _a !== void 0 ? _a : formatTimeFromMoment(endDate); }, [service.scheduledArrivalTime, endDate]);
    const allFlightServices = useMemo(() => props.cart.booking.services.filter(s => s.type == 'BookingFlightService'), [props.cart.booking.services]);
    const flightSegments = useMemo(() => allFlightServices.filter(s => !!(s.scheme || s.schemeId)), [allFlightServices]);
    const isModalHost = props.index === 0;
    const [placeEditorDialogOpen, setPlaceEditorDialogOpen] = useState(false);
    const [placeSelectorSelectedTab, setPlaceSelectorSelectedTab] = useState(0);
    const [selectedTouristIndex, setSelectedTouristIndex] = useState(undefined);
    const [segmentSelections, setSegmentSelections] = useState({});
    const [segmentUnavailable, setSegmentUnavailable] = useState({});
    const [segmentSchemes, setSegmentSchemes] = useState({});
    const [segmentScale, setSegmentScale] = useState({});
    const [segmentRotation, setSegmentRotation] = useState({});
    const activeSegment = (_d = flightSegments[placeSelectorSelectedTab]) !== null && _d !== void 0 ? _d : service;
    const activeScheme = activeSegment ? segmentSchemes[activeSegment.id] : undefined;
    const activeSegmentId = (_e = activeSegment === null || activeSegment === void 0 ? void 0 : activeSegment.id) !== null && _e !== void 0 ? _e : service.id;
    const activeScale = (_f = segmentScale[activeSegmentId]) !== null && _f !== void 0 ? _f : 1;
    const activeRotation = (_g = segmentRotation[activeSegmentId]) !== null && _g !== void 0 ? _g : 0;
    const isAircraftScheme = (activeScheme === null || activeScheme === void 0 ? void 0 : activeScheme.schemeType) === 'Aircraft';
    const bookerContainerRef = useRef(null);
    const sizeMultiplier = useMemo(() => {
        if (!activeScheme)
            return 1;
        if (activeScheme.schemeType === 'Aircraft')
            return 1;
        return Math.max(16 / activeScheme.data.bus.seatHeight, 16 / activeScheme.data.bus.seatWidth, 1000 / activeScheme.data.bus.width);
    }, [activeScheme]);
    const [size, setSize] = useState({ width: 1000, height: 1000 });
    const getTotalPersons = useCallback((s) => s.accommodation.adults + s.accommodation.childs, []);
    const activeTotalPersons = useMemo(() => getTotalPersons(activeSegment), [activeSegment, getTotalPersons]);
    const stageStyle = useMemo(() => ({
        width: isAircraftScheme ? size.width : '100%',
        maxWidth: isAircraftScheme ? 'none' : '100%',
        overflow: 'auto',
        maxHeight: dialogFullScreen ? '100%' : 'calc(100vh - 160px)',
    }), [dialogFullScreen, isAircraftScheme, size.width]);
    const selectionAvailable = !useCombinedFlightSchemeModal && isModalHost && flightSegments.length > 0;
    const activeSelections = activeSegment ? (segmentSelections[activeSegment.id] || []) : [];
    const activeUnavailableIds = activeSegment ? (segmentUnavailable[activeSegment.id] || []) : [];
    const calcSize = (currentScheme, multiplier = 1) => {
        var _a, _b, _c, _d;
        const containerWidth = (_b = (_a = bookerContainerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0;
        const containerHeight = (_d = (_c = bookerContainerRef.current) === null || _c === void 0 ? void 0 : _c.offsetHeight) !== null && _d !== void 0 ? _d : 0;
        const schemeWidth = (currentScheme ? ((currentScheme.data.bus.width * multiplier) + padding * 4) : 800);
        const schemeHeight = (currentScheme ? (((currentScheme.data.bus.height + currentScheme.data.bus.seatHeight) * currentScheme.data.bus.layers * multiplier) + padding * 8) : 450);
        return {
            width: containerWidth > 0 ? containerWidth : schemeWidth,
            height: containerHeight > 0 ? containerHeight : schemeHeight,
        };
    };
    const resize = useCallback(throttle(() => {
        const newSize = calcSize(activeScheme, sizeMultiplier);
        if (newSize.width !== size.width || newSize.height !== size.height) {
            requestAnimationFrame(() => setSize(newSize));
        }
    }, 100), [activeScheme, sizeMultiplier, size.width, size.height]);
    useLayoutEffect(() => {
        if (!bookerContainerRef.current)
            return;
        if (typeof ResizeObserver === 'undefined') {
            resize();
            return;
        }
        const observer = new ResizeObserver(() => resize());
        observer.observe(bookerContainerRef.current);
        return () => observer.disconnect();
    }, [resize]);
    useEffect(() => {
        window.addEventListener('resize', resize);
        resize();
        return () => {
            window.removeEventListener('resize', resize);
        };
    }, [resize]);
    useEffect(() => {
        if (placeEditorDialogOpen) {
            resize();
        }
    }, [placeEditorDialogOpen, dialogFullScreen, resize]);
    const normalizeScheme = (rawScheme) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0;
        if (!rawScheme)
            return undefined;
        const schemeTypeValue = (_b = (_a = rawScheme.schemeType) !== null && _a !== void 0 ? _a : rawScheme.SchemeType) !== null && _b !== void 0 ? _b : rawScheme.scheme_type;
        const normalizedType = typeof schemeTypeValue === 'string'
            ? schemeTypeValue.toLowerCase() === 'aircraft'
                ? 'Aircraft'
                : schemeTypeValue.toLowerCase() === 'bus'
                    ? 'Bus'
                    : undefined
            : schemeTypeValue === 1
                ? 'Aircraft'
                : schemeTypeValue === 0
                    ? 'Bus'
                    : undefined;
        const rawData = (_e = (_d = (_c = rawScheme.data) !== null && _c !== void 0 ? _c : rawScheme.Data) !== null && _d !== void 0 ? _d : rawScheme.schemeData) !== null && _e !== void 0 ? _e : rawScheme.SchemeData;
        const rawBus = (_f = rawData === null || rawData === void 0 ? void 0 : rawData.bus) !== null && _f !== void 0 ? _f : rawData === null || rawData === void 0 ? void 0 : rawData.Bus;
        const rawSeats = (_h = (_g = rawData === null || rawData === void 0 ? void 0 : rawData.seats) !== null && _g !== void 0 ? _g : rawData === null || rawData === void 0 ? void 0 : rawData.Seats) !== null && _h !== void 0 ? _h : [];
        const rawElements = (_k = (_j = rawData === null || rawData === void 0 ? void 0 : rawData.elements) !== null && _j !== void 0 ? _j : rawData === null || rawData === void 0 ? void 0 : rawData.Elements) !== null && _k !== void 0 ? _k : [];
        const rawConditions = (_o = (_m = (_l = rawData === null || rawData === void 0 ? void 0 : rawData.conditions) !== null && _l !== void 0 ? _l : rawData === null || rawData === void 0 ? void 0 : rawData.Conditions) !== null && _m !== void 0 ? _m : rawData === null || rawData === void 0 ? void 0 : rawData.placeConditions) !== null && _o !== void 0 ? _o : rawData === null || rawData === void 0 ? void 0 : rawData.PlaceConditions;
        const normalizedSeats = rawSeats.map((seat) => {
            var _a, _b;
            return (Object.assign(Object.assign({}, seat), { sectionId: (_a = seat === null || seat === void 0 ? void 0 : seat.sectionId) !== null && _a !== void 0 ? _a : seat === null || seat === void 0 ? void 0 : seat.SectionId, flightClassGroupId: (_b = seat === null || seat === void 0 ? void 0 : seat.flightClassGroupId) !== null && _b !== void 0 ? _b : seat === null || seat === void 0 ? void 0 : seat.FlightClassGroupId }));
        });
        const rawSections = (_p = rawBus === null || rawBus === void 0 ? void 0 : rawBus.sections) !== null && _p !== void 0 ? _p : rawBus === null || rawBus === void 0 ? void 0 : rawBus.Sections;
        const inferredType = normalizedType
            ? normalizedType
            : (Array.isArray(rawSections) && rawSections.length > 0)
                || normalizedSeats.some((s) => !!(s === null || s === void 0 ? void 0 : s.sectionId) || (s === null || s === void 0 ? void 0 : s.flightClassGroupId) !== undefined)
                ? 'Aircraft'
                : 'Bus';
        const seats = inferredType === 'Aircraft' && !normalizedSeats.some((s) => !!(s === null || s === void 0 ? void 0 : s.sectionId) || (s === null || s === void 0 ? void 0 : s.flightClassGroupId) !== undefined)
            ? normalizedSeats.map((seat) => { var _a; return (Object.assign(Object.assign({}, seat), { sectionId: (_a = seat === null || seat === void 0 ? void 0 : seat.sectionId) !== null && _a !== void 0 ? _a : 'section-1' })); })
            : normalizedSeats;
        const normalizedBus = rawBus ? Object.assign(Object.assign({}, rawBus), { width: (_q = rawBus === null || rawBus === void 0 ? void 0 : rawBus.width) !== null && _q !== void 0 ? _q : rawBus === null || rawBus === void 0 ? void 0 : rawBus.Width, height: (_r = rawBus === null || rawBus === void 0 ? void 0 : rawBus.height) !== null && _r !== void 0 ? _r : rawBus === null || rawBus === void 0 ? void 0 : rawBus.Height, seatWidth: (_s = rawBus === null || rawBus === void 0 ? void 0 : rawBus.seatWidth) !== null && _s !== void 0 ? _s : rawBus === null || rawBus === void 0 ? void 0 : rawBus.SeatWidth, seatHeight: (_t = rawBus === null || rawBus === void 0 ? void 0 : rawBus.seatHeight) !== null && _t !== void 0 ? _t : rawBus === null || rawBus === void 0 ? void 0 : rawBus.SeatHeight, seatFrontSpacing: (_u = rawBus === null || rawBus === void 0 ? void 0 : rawBus.seatFrontSpacing) !== null && _u !== void 0 ? _u : rawBus === null || rawBus === void 0 ? void 0 : rawBus.SeatFrontSpacing, seatSideSpacing: (_v = rawBus === null || rawBus === void 0 ? void 0 : rawBus.seatSideSpacing) !== null && _v !== void 0 ? _v : rawBus === null || rawBus === void 0 ? void 0 : rawBus.SeatSideSpacing, gridSize: (_w = rawBus === null || rawBus === void 0 ? void 0 : rawBus.gridSize) !== null && _w !== void 0 ? _w : rawBus === null || rawBus === void 0 ? void 0 : rawBus.GridSize, layers: (_x = rawBus === null || rawBus === void 0 ? void 0 : rawBus.layers) !== null && _x !== void 0 ? _x : rawBus === null || rawBus === void 0 ? void 0 : rawBus.Layers }) : rawBus;
        return Object.assign(Object.assign({}, rawScheme), { schemeType: inferredType, data: rawData ? Object.assign(Object.assign({}, rawData), { bus: (_y = normalizedBus !== null && normalizedBus !== void 0 ? normalizedBus : rawData === null || rawData === void 0 ? void 0 : rawData.bus) !== null && _y !== void 0 ? _y : rawData === null || rawData === void 0 ? void 0 : rawData.Bus, seats, elements: rawElements, conditions: rawConditions }) : rawScheme.data, created: dayjs((_z = rawScheme.created) !== null && _z !== void 0 ? _z : rawScheme.Created), updated: rawScheme.updated || rawScheme.Updated ? dayjs((_0 = rawScheme.updated) !== null && _0 !== void 0 ? _0 : rawScheme.Updated) : undefined });
    };
    const loadFlightConfiguration = useCallback((segment) => __awaiter(this, void 0, void 0, function* () {
        if (!segment)
            return;
        if (segment.scheme) {
            setSegmentSchemes(prev => (Object.assign(Object.assign({}, prev), { [segment.id]: normalizeScheme(segment.scheme) })));
            setSegmentUnavailable(prev => { var _a; return (Object.assign(Object.assign({}, prev), { [segment.id]: (_a = segment.bookedPlaceIds) !== null && _a !== void 0 ? _a : [] })); });
            return;
        }
        if (!segment.schemeId)
            return;
        const { response } = yield api('get', 'FlightStatic/SchemeConfiguration', {
            schemeId: segment.schemeId,
            flightId: segment.flightId,
            flightDate: moment(segment.begin).format('YYYY-MM-DD'),
        });
        if (response === null || response === void 0 ? void 0 : response.scheme) {
            setSegmentSchemes(prev => (Object.assign(Object.assign({}, prev), { [segment.id]: normalizeScheme(response.scheme) })));
            setSegmentUnavailable(prev => { var _a; return (Object.assign(Object.assign({}, prev), { [segment.id]: (_a = response.bookedPlaceIds) !== null && _a !== void 0 ? _a : [] })); });
        }
    }), [normalizeScheme]);
    useEffect(() => {
        if (!selectionAvailable)
            return;
        flightSegments.forEach(segment => {
            if (!segmentSchemes[segment.id])
                loadFlightConfiguration(segment);
        });
    }, [flightSegments, loadFlightConfiguration, segmentSchemes, selectionAvailable]);
    useEffect(() => {
        if (Object.values(segmentSchemes).some(s => (s === null || s === void 0 ? void 0 : s.schemeType) === 'Aircraft'))
            setForceFullScreen(true);
    }, [segmentSchemes]);
    useEffect(() => {
        if (!selectionAvailable || !flightSegments.length)
            return;
        if (!Object.keys(segmentSelections).length)
            return;
        flightSegments.forEach(segment => {
            const scheme = segmentSchemes[segment.id];
            const selected = segmentSelections[segment.id] || [];
            const tourists = segment.tourists || [];
            if (!scheme)
                return;
            const normalizePlaces = (places) => {
                return (places || [])
                    .filter(p => p.serviceId === segment.id)
                    .map(p => {
                    var _a, _b, _c;
                    return ({
                        serviceId: p.serviceId,
                        placeId: (_a = p.placeId) !== null && _a !== void 0 ? _a : null,
                        placeName: (_b = p.placeName) !== null && _b !== void 0 ? _b : null,
                        bookableId: (_c = p.bookableId) !== null && _c !== void 0 ? _c : null,
                    });
                })
                    .sort((a, b) => a.serviceId - b.serviceId);
            };
            const samePlaces = (a, b) => {
                var _a, _b, _c, _d, _e, _f;
                if (a.length !== b.length)
                    return false;
                for (let i = 0; i < a.length; i++) {
                    const left = a[i];
                    const right = b[i];
                    if (left.serviceId !== right.serviceId)
                        return false;
                    if (((_a = left.placeId) !== null && _a !== void 0 ? _a : null) !== ((_b = right.placeId) !== null && _b !== void 0 ? _b : null))
                        return false;
                    if (((_c = left.bookableId) !== null && _c !== void 0 ? _c : null) !== ((_d = right.bookableId) !== null && _d !== void 0 ? _d : null))
                        return false;
                    if (((_e = left.placeName) !== null && _e !== void 0 ? _e : null) !== ((_f = right.placeName) !== null && _f !== void 0 ? _f : null))
                        return false;
                }
                return true;
            };
            const updates = tourists.map((t, ti) => {
                var _a, _b;
                const retained = (t.flightPlaces || []).filter(p => p.serviceId !== segment.id);
                const selectedSeat = selected[ti];
                const seat = scheme.data.seats.find(s => s.id == selectedSeat);
                const prev = (t.flightPlaces || []).find(p => p.serviceId === segment.id);
                const updated = selectedSeat ? [{
                        serviceId: segment.id,
                        placeId: selectedSeat,
                        placeName: (_a = seat === null || seat === void 0 ? void 0 : seat.name) !== null && _a !== void 0 ? _a : prev === null || prev === void 0 ? void 0 : prev.placeName,
                        bookableId: (_b = seat === null || seat === void 0 ? void 0 : seat.bookableId) !== null && _b !== void 0 ? _b : prev === null || prev === void 0 ? void 0 : prev.bookableId,
                    }] : [];
                const nextPlaces = [...retained, ...updated];
                const currentSegmentPlaces = normalizePlaces(t.flightPlaces);
                const nextSegmentPlaces = normalizePlaces(nextPlaces);
                if (samePlaces(currentSegmentPlaces, nextSegmentPlaces))
                    return null;
                return {
                    personIdx: ti,
                    tourist: {
                        flightPlaces: nextPlaces,
                    }
                };
            }).filter(Boolean);
            if (updates.length > 0)
                props.onTouristsChange(segment.id, updates);
        });
    }, [flightSegments, segmentSchemes, segmentSelections, selectionAvailable]);
    const normalizeSelection = useCallback((selected, total) => {
        const normalized = selected.map(s => s !== null && s !== void 0 ? s : undefined);
        if (normalized.length >= total)
            return normalized;
        return [
            ...normalized,
            ...Array.from({ length: total - normalized.length }).map(() => undefined),
        ];
    }, []);
    const openPlaceSelectorDialog = useCallback((targetServiceId) => {
        if (!selectionAvailable)
            return;
        const selections = {};
        flightSegments.forEach(segment => {
            const total = getTotalPersons(segment);
            const values = (segment.tourists || []).map(t => {
                var _a, _b;
                const existing = (_b = (_a = t.flightPlaces) === null || _a === void 0 ? void 0 : _a.find(p => p.serviceId === segment.id)) === null || _b === void 0 ? void 0 : _b.placeId;
                if (existing)
                    return existing;
                return t.busPlaceId || undefined;
            });
            selections[segment.id] = normalizeSelection(values, total);
        });
        const pendingIndex = flightSegments.findIndex(segment => {
            const selected = selections[segment.id] || [];
            const total = getTotalPersons(segment);
            return selected.length < total || selected.some(s => !s);
        });
        const targetIndex = targetServiceId ? flightSegments.findIndex(segment => segment.id === targetServiceId) : -1;
        const nextIndex = targetIndex >= 0 ? targetIndex : (pendingIndex >= 0 ? pendingIndex : 0);
        setSegmentSelections(selections);
        setSelectedTouristIndex(undefined);
        setPlaceSelectorSelectedTab(nextIndex);
        setPlaceEditorDialogOpen(true);
    }, [flightSegments, getTotalPersons, normalizeSelection, selectionAvailable]);
    const resetSelection = useCallback(() => {
        setPlaceEditorDialogOpen(false);
    }, []);
    useEffect(() => {
        if (useCombinedFlightSchemeModal)
            return;
        const handler = (e) => {
            var _a;
            const targetServiceId = (_a = e === null || e === void 0 ? void 0 : e.detail) === null || _a === void 0 ? void 0 : _a.serviceId;
            if (targetServiceId && !flightSegments.some(segment => segment.id === targetServiceId))
                return;
            openPlaceSelectorDialog(targetServiceId);
        };
        if (!isModalHost)
            return;
        document.addEventListener('flightSchemeModalRequested', handler);
        return () => document.removeEventListener('flightSchemeModalRequested', handler);
    }, [flightSegments, isModalHost, openPlaceSelectorDialog, useCombinedFlightSchemeModal]);
    const placesValid = useMemo(() => {
        if (!selectionAvailable)
            return true;
        return flightSegments.every(segment => {
            const total = getTotalPersons(segment);
            const selected = segmentSelections[segment.id] || [];
            if (selected.length < total)
                return false;
            if (selected.some(s => !s))
                return false;
            return true;
        });
    }, [flightSegments, getTotalPersons, segmentSelections, selectionAvailable]);
    const onSelectSeat = (p) => {
        if (!activeSegment)
            return;
        const selected = activeSelections;
        if (selectedTouristIndex !== undefined && selected.length > selectedTouristIndex) {
            const next = [
                ...selected.slice(0, selectedTouristIndex),
                selected[selectedTouristIndex] != p.id ? p.id : undefined,
                ...selected.slice(selectedTouristIndex + 1),
            ];
            setSegmentSelections(prev => (Object.assign(Object.assign({}, prev), { [activeSegment.id]: next })));
            return;
        }
        const oldIndex = selected.indexOf(p.id);
        if (oldIndex >= 0) {
            const next = [...selected.slice(0, oldIndex), undefined, ...selected.slice(oldIndex + 1)];
            setSegmentSelections(prev => (Object.assign(Object.assign({}, prev), { [activeSegment.id]: next })));
            return;
        }
        const emptyIndex = selected.findIndex(s => !s);
        if (emptyIndex < 0)
            return;
        const next = [...selected.slice(0, emptyIndex), p.id, ...selected.slice(emptyIndex + 1)];
        setSegmentSelections(prev => (Object.assign(Object.assign({}, prev), { [activeSegment.id]: next })));
    };
    const totalPersonsLabel = useMemo(() => {
        const total = service.accommodation.adults + service.accommodation.childs;
        return `${total} ${pluralizeAutoSplit(total, _T.COMMON_PERSONS)}`;
    }, [service.accommodation.adults, service.accommodation.childs]);
    const flightClassGroupId = isAircraftScheme ? (_h = activeSegment === null || activeSegment === void 0 ? void 0 : activeSegment.flightClass) === null || _h === void 0 ? void 0 : _h.id : undefined;
    const seatMatchesFlightClass = useCallback((seat) => {
        if (!flightClassGroupId)
            return true;
        if (seat.flightClassGroupId === undefined || seat.flightClassGroupId === null)
            return false;
        return seat.flightClassGroupId === flightClassGroupId;
    }, [flightClassGroupId]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "table-responsive" },
            React.createElement("div", { className: "ajax-replace" },
                React.createElement("table", { className: "table order-table" },
                    props.index == 0 ?
                        React.createElement("thead", null,
                            React.createElement("tr", null,
                                React.createElement("th", null,
                                    !displayFlightAsTransportation ? React.createElement("span", { className: "order-table-icon order-table-icon-flight", "aria-hidden": "true" }) : React.createElement("span", { className: "order-table-icon order-table-icon-train", "aria-hidden": "true" }),
                                    " ",
                                    !displayFlightAsTransportation ? _T.COMMON_FLIGHT : _T.COMMON_TRANSPORTATION),
                                React.createElement("th", null, _T.COMMON_DATE),
                                showClass ? React.createElement("th", null, _T.FLIGHT_CLASS) : null,
                                React.createElement("th", { className: "transfer-cost" }, _T.BOOKING_TOTAL_PRICE),
                                React.createElement("th", null, _T.COMMON_PERSONCOUNT),
                                React.createElement("th", { className: "order-status" }, _T.COMMON_AVAILABILITY))) : null,
                    React.createElement("tbody", null,
                        React.createElement("tr", { className: `order-info ajax-replace service${service.id} service`, id: `flightServiceRow${service.id}`, "data-service-id": service.id },
                            React.createElement("td", { className: "order-flight-name" },
                                React.createElement("span", { className: "order-hotel" }, (_j = service.cityFrom) === null || _j === void 0 ? void 0 :
                                    _j.name,
                                    " \u2014 ", (_k = service.cityTo) === null || _k === void 0 ? void 0 :
                                    _k.name),
                                !displayFlightAsTransportation && (!!service.flightNumber || !!service.airlineName) ? React.createElement(React.Fragment, null,
                                    React.createElement("br", null),
                                    _T.FLIGHT_NUMBER,
                                    ": ",
                                    service.flightNumber,
                                    " ",
                                    service.airlineName ? `- ${service.airlineName}` : '') : null),
                            React.createElement("td", { className: "flight-date" }, beginDate.isValid() && endDate.isValid()
                                ? (React.createElement("label", { className: "flight-datetime-container" },
                                    React.createElement("span", { className: "flight-info-part" },
                                        beginTime ? React.createElement("span", { className: "time" }, beginTime) : null,
                                        React.createElement("span", { className: "date" }, beginDate.format('L'))),
                                    React.createElement("span", { className: "flight-datetime-separator" }, "\u2014"),
                                    React.createElement("span", { className: "flight-info-part" },
                                        endTime ? React.createElement("span", { className: "time" }, endTime) : null,
                                        React.createElement("span", { className: "date" }, endDate.format('L')))))
                                : (React.createElement(React.Fragment, null,
                                    beginDate.isValid() ? beginDate.format('L') : '',
                                    React.createElement("span", { className: "text-grey text-uppercase" }, beginDate.isValid() ? beginDate.format('ddd') : '')))),
                            showClass ? (React.createElement("td", { className: "flight-class" },
                                React.createElement("span", { className: "text-uppercase" }, (_l = service.flightClass) === null || _l === void 0 ? void 0 : _l.name),
                                React.createElement(InternalFlightBaggageCompact, { luggageDescription: service.luggageDescription }))) : null,
                            React.createElement("td", { className: "flight-cost" },
                                formatMoney(service.priceInBookingCurrency, { currency: props.cart.booking.currency.name }),
                                " ",
                                props.cart.booking.currency.name),
                            React.createElement("td", { className: "flight-hum" }, totalPersonsLabel),
                            React.createElement("td", { className: "order-status immediate" }, _T.ROOM_AVAILABLE)),
                        React.createElement("tr", { className: `order-info-bottom service${service.id}` },
                            React.createElement("td", { colSpan: deleteColSpan }),
                            React.createElement("td", { className: "order-del-bottom" },
                                React.createElement("a", { href: "#", className: "link delete-service", "data-service-id": service.id, onClick: () => props.deleteService(service.id) }, _T.BOOKING_DELETE_SERVICE))))))),
        showExtendedInfo ? React.createElement(React.Fragment, null,
            React.createElement("a", { href: `#flightInfo${service.id}`, className: `orange-collapse-link collapse service${service.id} d-block`, "data-toggle": "collapse", "aria-expanded": "false", "aria-controls": `flightInfo${service.id}` },
                React.createElement("span", { className: "caret-control" }, _T.BOOKING_FLIGHT_INFORMATION)),
            React.createElement("div", { className: `collapse dop-info service${service.id}`, id: `flightInfo${service.id}` },
                React.createElement("p", { dangerouslySetInnerHTML: { __html: service.flightInfo.notes } }))) : null,
        useExtendedCartPageDesign || isCapitalUI ? (React.createElement("div", { className: `arrived service${service.id}` },
            React.createElement(ServiceTouristsBlock, { cart: props.cart, service: service, onTouristChange: props.onTouristChange }))) : (React.createElement(React.Fragment, null,
            React.createElement("a", { href: `#moreInfo${service.id}`, className: `tourist-list orange-collapse-link collapse service${service.id} d-block`, "data-toggle": "collapse", "aria-expanded": "false", "aria-controls": `moreInfo${service.id}` },
                React.createElement("span", { className: "caret-control" }, _T.BOOKING_TOURIST_DATA_ENTER)),
            React.createElement("div", { className: `arrived collapse service${service.id}`, id: `moreInfo${service.id}` },
                React.createElement(ServiceTouristsBlock, { cart: props.cart, service: service, onTouristChange: props.onTouristChange })))),
        selectionAvailable ? (React.createElement(Dialog, { open: !!placeEditorDialogOpen, onClose: () => resetSelection(), maxWidth: dialogFullScreen ? undefined : 'lg', fullScreen: dialogFullScreen, TransitionProps: { onEntered: () => resize() } },
            React.createElement(DialogTitle, { disableTypography: true, style: { padding: dialogFullScreen ? '12px 16px' : undefined } },
                React.createElement("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' } },
                    React.createElement(Typography, { variant: "h6" }, "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043C\u0435\u0441\u0442\u0430 \u0434\u043B\u044F \u043F\u0430\u0441\u0441\u0430\u0436\u0438\u0440\u043E\u0432"),
                    React.createElement(Tooltip, { title: forceFullScreen ? 'Обычный режим' : 'На весь экран' },
                        React.createElement(IconButton, { size: "small", onClick: () => setForceFullScreen(prev => !prev) }, forceFullScreen ? React.createElement(FullscreenExitIcon, null) : React.createElement(FullscreenIcon, null))))),
            React.createElement(DialogContent, { ref: bookerContainerRef, style: { overflow: 'hidden', height: dialogFullScreen ? 'calc(100vh - 120px)' : 'calc(100vh - 160px)' }, classes: { root: 'p-0' } },
                flightSegments.length > 1 ? (React.createElement(Box, { px: 2, pt: 1, pb: 0 },
                    React.createElement(Tabs, { value: placeSelectorSelectedTab, onChange: (_, value) => setPlaceSelectorSelectedTab(value), variant: "scrollable", scrollButtons: "auto" }, flightSegments.map(segment => {
                        var _a, _b;
                        const from = (_a = segment.cityFrom) === null || _a === void 0 ? void 0 : _a.name;
                        const to = (_b = segment.cityTo) === null || _b === void 0 ? void 0 : _b.name;
                        const baseName = from && to ? `${from} — ${to}` : _T.COMMON_FLIGHT;
                        const dateLabel = moment(segment.begin).isValid() ? moment(segment.begin).format('L') : '';
                        const label = dateLabel ? `${baseName} • ${dateLabel}` : baseName;
                        return React.createElement(Tab, { key: segment.id, label: label });
                    })))) : null,
                activeScheme ? (React.createElement(BookerBase, { id: `flight-booker-stage-${(_m = activeSegment === null || activeSegment === void 0 ? void 0 : activeSegment.id) !== null && _m !== void 0 ? _m : service.id}`, stageStyle: stageStyle, topLeftContent: React.createElement("div", { style: { marginTop: padding / 2, width: '230px' } },
                        React.createElement(BookerViewControls, { scale: activeScale, rotation: activeRotation, onScaleChange: (nextScale) => setSegmentScale(prev => (Object.assign(Object.assign({}, prev), { [activeSegmentId]: nextScale }))), onRotationChange: (nextRotation) => setSegmentRotation(prev => (Object.assign(Object.assign({}, prev), { [activeSegmentId]: nextRotation }))) }),
                        React.createElement(Select, { formControlProps: {
                                style: { width: '100%' }
                            }, labelClassName: 'MuiInputLabel-shrink', className: 'my-2', variant: 'filled', label: _T.COMMON_TOURIST, value: selectedTouristIndex === undefined ? -1 : selectedTouristIndex, onChange: (e) => {
                                const v = Number.parseInt(e.target.value);
                                setSelectedTouristIndex(v == -1 ? undefined : v);
                            }, inputProps: {
                                name: 'touristIndex',
                                id: 'touristIndex'
                            } }, [{ name: _T.COMMON_ALL, index: -1 }, ...((activeSegment === null || activeSegment === void 0 ? void 0 : activeSegment.tourists) || []).map((t, i) => {
                                var _a, _b, _c;
                                return ({
                                    name: `${(_a = t.surname) !== null && _a !== void 0 ? _a : ''} ${(_b = t.name) !== null && _b !== void 0 ? _b : ''} ${(_c = t.middleName) !== null && _c !== void 0 ? _c : ''}`.trim() || `${_T.COMMON_ADULT} ${i + 1}`,
                                    index: i
                                });
                            })].map(o => (React.createElement("option", { key: o.index, value: o.index }, o.name))))), data: activeScheme.data, sizeMultiplier: sizeMultiplier, size: size, showControls: false, scale: activeScale, onScaleChange: (nextScale) => setSegmentScale(prev => (Object.assign(Object.assign({}, prev), { [activeSegmentId]: nextScale }))), rotation: activeRotation, onRotationChange: (nextRotation) => setSegmentRotation(prev => (Object.assign(Object.assign({}, prev), { [activeSegmentId]: nextRotation }))), allowChoiceFunc: p => {
                        if (flightClassGroupId && !seatMatchesFlightClass(p) && !activeSelections.includes(p.id))
                            return false;
                        if (selectedTouristIndex !== undefined) {
                            const unavailable = [
                                ...activeUnavailableIds,
                                ...activeSelections.filter((t, i) => i != selectedTouristIndex && !!t),
                            ];
                            return unavailable.findIndex(ux => ux === p.id && ux != activeSelections[selectedTouristIndex]) < 0;
                        }
                        if (activeSelections.length == activeTotalPersons && !activeSelections.filter(s => !s).length && activeSelections.findIndex(s => s == p.id) < 0)
                            return false;
                        return activeUnavailableIds.findIndex(ux => ux === p.id && !activeSelections.includes(ux)) < 0;
                    }, availableFunc: p => {
                        if (flightClassGroupId && !seatMatchesFlightClass(p) && !activeSelections.includes(p.id))
                            return false;
                        if (selectedTouristIndex !== undefined) {
                            const unavailable = [
                                ...activeUnavailableIds,
                                ...activeSelections.filter((t, i) => i != selectedTouristIndex && !!t),
                            ];
                            return unavailable.findIndex(ux => ux === p.id && ux != activeSelections[selectedTouristIndex]) < 0;
                        }
                        return activeUnavailableIds.findIndex(ux => ux === p.id && !activeSelections.includes(ux)) < 0;
                    }, selectedFunc: p => {
                        if (selectedTouristIndex !== undefined)
                            return activeSelections[selectedTouristIndex] == p.id;
                        return activeSelections.includes(p.id);
                    }, renderSeatPopupContent: renderSeatPricePopup(activeSegment, activeSegmentId, props.cart.booking.currency.name), onSelect: (p) => onSelectSeat(p), onDrag: isAircraftScheme ? undefined : (pos => {
                        var _a;
                        const panel = document.querySelector(`#flight-booker-stage-${(_a = activeSegment === null || activeSegment === void 0 ? void 0 : activeSegment.id) !== null && _a !== void 0 ? _a : service.id} .konvajs-content`);
                        const parent = panel === null || panel === void 0 ? void 0 : panel.parentElement;
                        if (parent)
                            parent.scrollBy({ top: -pos.y, left: -pos.x, behavior: 'auto' });
                        return { x: padding * 2, y: 0 };
                    }) })) : (React.createElement(AsyncSpinner, null))),
            React.createElement(DialogActions, { className: "p-0" },
                React.createElement(Box, { className: "payment-buttons", style: { padding: '0 24px' } },
                    React.createElement(Box, { className: "payment-button payment-button-cancel" },
                        React.createElement(Button, { variant: "outlined", color: "default", onClick: resetSelection }, _T.COMMON_CANCEL_2)),
                    React.createElement(Box, { className: "payment-button payment-button-confirm" }, placesValid
                        ? React.createElement(Button, { variant: "contained", color: "primary", onClick: () => {
                                setPlaceEditorDialogOpen(false);
                                setTimeout(() => cartOrder.makeBooking(), 300);
                            } },
                            _T.COMMON_CONTINUE,
                            " \u00BB")
                        : React.createElement(Tooltip, { title: React.createElement(Typography, { variant: 'body2' }, "\u0427\u0442\u043E\u0431\u044B \u043F\u0440\u043E\u0434\u043E\u043B\u0436\u0438\u0442\u044C, \u0432\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043C\u0435\u0441\u0442\u0430 \u0434\u043B\u044F \u0432\u0441\u0435\u0445 \u043F\u0430\u0441\u0441\u0430\u0436\u0438\u0440\u043E\u0432"), placement: "bottom" },
                            React.createElement(Button, { variant: "contained", color: "primary", disabled: true, style: { pointerEvents: 'auto' } },
                                _T.COMMON_CONTINUE,
                                " \u00BB"))))))) : null));
}
